import { useState, useCallback, useEffect } from "react";
import { memoryStore, setItemImmediate } from "../utils/storage";

const LOG_KEY = "pension_activity_log_v1";
const LEGACY_NOTES_KEY = "pension_general_notes_v1";

// סוגים שתועדו אוטומטית בעבר (טעינת קובץ / פתיחת דוח) — לא מתועדים יותר
const RETIRED_TYPES = ["load", "report"];

function loadLog() {
  try {
    const raw = memoryStore.getItem(LOG_KEY);
    const entries = raw ? JSON.parse(raw) : [];
    const cleaned = entries.filter(e => !RETIRED_TYPES.includes(e.type));
    if (cleaned.length !== entries.length) saveLog(cleaned);
    return cleaned;
  } catch { return []; }
}

function saveLog(entries) {
  try { setItemImmediate(LOG_KEY, JSON.stringify(entries)); } catch {}
}

/**
 * מיגרציה חד-פעמית: הערות מהאחסון הישן (pension_general_notes_v1, לפי clientId)
 * הופכות לרשומות יומן type="note". אידמפוטנטית — דדופ לפי id, והמפתח הישן נמחק בסיום.
 */
export function migrateLegacyNotes(clients) {
  try {
    const raw = memoryStore.getItem(LEGACY_NOTES_KEY);
    if (!raw) return false;
    const byClient = JSON.parse(raw);
    if (!byClient || typeof byClient !== "object") {
      memoryStore.removeItem(LEGACY_NOTES_KEY);
      return false;
    }

    const log = loadLog();
    const existingIds = new Set(log.map(e => e.id));
    const migrated = [];

    for (const [clientId, notes] of Object.entries(byClient)) {
      const clientName = (clients || []).find(c => c.id === clientId)?.fileInfo?.client;
      for (const n of notes || []) {
        if (!n || existingIds.has(n.id)) continue;
        migrated.push({
          id: n.id,
          type: "note",
          description: n.text,
          ...(clientName ? { clientName } : {}),
          date: (n.createdAt || "").slice(0, 10) || new Date().toISOString().slice(0, 10),
          createdAt: n.createdAt || new Date().toISOString(),
          ...(n.reminderDate ? { reminder: n.reminderDate } : {}),
        });
      }
    }

    if (migrated.length) saveLog([...migrated, ...log]);
    memoryStore.removeItem(LEGACY_NOTES_KEY);
    return migrated.length > 0;
  } catch { return false; }
}

export function useActivityLog() {
  const [entries, setEntries] = useState(() => loadLog());

  useEffect(() => {
    const onSync = () => setEntries(loadLog());
    window.addEventListener("pension-data-synced", onSync);
    return () => window.removeEventListener("pension-data-synced", onSync);
  }, []);

  const reload = useCallback(() => setEntries(loadLog()), []);

  const addEntry = useCallback((entry) => {
    setEntries(prev => {
      const next = [
        {
          id: `log-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
          date: new Date().toISOString().slice(0, 10),
          createdAt: new Date().toISOString(),
          ...entry,
        },
        ...prev,
      ];
      saveLog(next);
      return next;
    });
  }, []);

  const updateEntry = useCallback((id, updates) => {
    setEntries(prev => {
      const next = prev.map(e => e.id === id ? { ...e, ...updates } : e);
      saveLog(next);
      return next;
    });
  }, []);

  const deleteEntry = useCallback((id) => {
    setEntries(prev => {
      const next = prev.filter(e => e.id !== id);
      saveLog(next);
      return next;
    });
  }, []);

  return { entries, addEntry, updateEntry, deleteEntry, reload };
}
